/**
 * LoadingOverlay - Reusable loading indicator component
 *
 * Provides consistent loading states across the app:
 * - Full screen overlay with optional message
 * - Inline loading for buttons
 * - Centered loading for containers
 */

import React from 'react';
import { View, ActivityIndicator, StyleSheet } from 'react-native';

import CustomText from './CustomText';

/**
 * Loading overlay component
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether to show the loading indicator
 * @param {'small' | 'large'} [props.size='large'] - Size of the indicator
 * @param {string} [props.message] - Optional loading message
 * @param {boolean} [props.overlay=false] - Show as full-screen overlay
 * @param {boolean} [props.inline=false] - Show inline (for buttons)
 * @param {string} [props.color='#FFC451'] - Indicator color
 * @param {Object} [props.style] - Additional container styles
 *
 * @example
 * // Full screen loading
 * if (loading) return <LoadingOverlay visible />;
 *
 * @example
 * // With message
 * <LoadingOverlay visible message="Loading reports..." />
 *
 * @example
 * // Inline in button
 * <Text>Submit {isSubmitting && <LoadingOverlay visible inline size="small" />}</Text>
 *
 * @example
 * // As overlay on content
 * <View>
 *   <Content />
 *   <LoadingOverlay visible={loading} overlay />
 * </View>
 */
export default function LoadingOverlay({
  visible = true,
  size = 'large',
  message,
  overlay = false,
  inline = false,
  color = '#FFC451',
  style,
}) {
  if (!visible) return null;

  // Inline mode for buttons
  if (inline) {
    return <ActivityIndicator size={size} color={color} />;
  }

  // Full screen overlay
  if (overlay) {
    return (
      <View style={[styles.overlay, style]}>
        <View style={styles.overlayContent}>
          <ActivityIndicator size={size} color={color} />
          {message && (
            <CustomText style={styles.message} size={16} color="#666">
              {message}
            </CustomText>
          )}
        </View>
      </View>
    );
  }

  // Centered container (default)
  return (
    <View style={[styles.container, style]}>
      <ActivityIndicator size={size} color={color} />
      {message && (
        <CustomText style={styles.message} size={16} color="#666">
          {message}
        </CustomText>
      )}
    </View>
  );
}

/**
 * Simple inline loading indicator for buttons
 * Shorthand for <LoadingOverlay visible inline size="small" />
 * Default color is black for visibility on yellow buttons
 */
export function InlineLoader({ visible = true, color = '#000' }) {
  if (!visible) return null;
  return <ActivityIndicator size="small" color={color} />;
}

/**
 * Full screen loading state
 * Shorthand for <LoadingOverlay visible size="large" />
 */
export function FullScreenLoader({ visible = true, message, color = '#FFC451' }) {
  if (!visible) return null;
  return (
    <View style={styles.fullScreen}>
      <ActivityIndicator size="large" color={color} />
      {message && (
        <CustomText style={styles.message} size={16} color="#666">
          {message}
        </CustomText>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  fullScreen: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  overlayContent: {
    alignItems: 'center',
    padding: 20,
  },
  message: {
    marginTop: 12,
    textAlign: 'center',
  },
});
